import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listConfigKVs,
  getConfigKV,
  createConfigKV,
  updateConfigKV,
  deleteConfigKV,
  bulkCreateConfigKVs,
  bulkDeleteConfigKVs,
  exportConfigKVs,
  importConfigKVs,
  ConfigKV,
  ConfigKVCreate,
  ConfigKVUpdate,
  ConfigKVBulkCreate,
  ConfigKVBulkDelete
} from '@/app/services/configKvService'
import { QueryParams } from '@/app/types/common'

const QUERY_KEY = 'configKVs'

export const useConfigKVs = (
  params: QueryParams & {
    scope?: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
    search?: string
  }
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'list', params],
    queryFn: () => listConfigKVs(params),
    enabled: !!params.organization_id || params.scope === 'global'
  })
}

export const useConfigKV = (
  key: string | null,
  params: {
    scope: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
  }
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', key, params],
    queryFn: () => getConfigKV(key!, params),
    enabled: !!key && (!!params.organization_id || params.scope === 'global')
  })
}

export const useCreateConfigKV = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createConfigKV,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      toast.success(`Config key "${variables.key}" created successfully`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create config key')
    }
  })
}

export const useUpdateConfigKV = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, key, params, data }: {
      id: number
      key?: string
      params: {
        scope: 'global' | 'organization' | 'site'
        organization_id?: number
        site_id?: number
      }
      data: ConfigKVUpdate
    }) => updateConfigKV(id, params, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      toast.success(`Configuration updated successfully`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update config key')
    }
  })
}

export const useDeleteConfigKV = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ key, params }: {
      key: string
      params: {
        scope: 'global' | 'organization' | 'site'
        organization_id?: number
        site_id?: number
      }
    }) => deleteConfigKV(key, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      toast.success(`Config key "${variables.key}" deleted successfully`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete config key')
    }
  })
}

export const useBulkCreateConfigKVs = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkCreateConfigKVs,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      toast.success(`${data.length} config keys created successfully`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create config keys')
    }
  })
}

export const useBulkDeleteConfigKVs = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkDeleteConfigKVs,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      toast.success(`${variables.keys.length} config keys deleted successfully`)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete config keys')
    }
  })
}

export const useExportConfigKVs = () => {
  return useMutation({
    mutationFn: exportConfigKVs,
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `config-kv-export-${new Date().toISOString()}.${variables.format || 'json'}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Config keys exported successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to export config keys')
    }
  })
}

export const useImportConfigKVs = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, params }: {
      file: File
      params: {
        scope: 'global' | 'organization' | 'site'
        organization_id?: number
        site_id?: number
      }
    }) => importConfigKVs(file, params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      if (data.errors.length > 0) {
        toast.warning(`Imported ${data.imported} config keys with ${data.errors.length} errors`)
      } else {
        toast.success(`Successfully imported ${data.imported} config keys`)
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to import config keys')
    }
  })
}